import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import siteContent from '../../content/site/content.json';
import { X, Check, Info, ArrowRight } from 'lucide-react';

const UseCaseSection = () => {
  const navigate = useNavigate();
  const d = siteContent.useCase;
  const scenarios = d?.scenarios || [];
  const [active, setActive] = useState(0);
  if (!d || scenarios.length === 0) return null;

  const s = scenarios[active];

  return (
    <section id="use-case" className="py-16 lg:py-24 bg-[#FFF8F5]" data-testid="use-case-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-4">
            <span className="w-8 h-px bg-[#B5541B]" />
            {d.badge}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] leading-tight mb-3">{d.heading}</h2>
          <p className="text-base sm:text-lg text-[#3B3535]/70 leading-relaxed">{d.intro}</p>
        </div>

        {/* Scenario tabs */}
        <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Resident scenarios">
          {scenarios.map((sc, i) => (
            <button
              key={sc.tabLabel}
              role="tab"
              aria-selected={i === active}
              data-testid={`use-case-tab-${i}`}
              onClick={() => setActive(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                i === active
                  ? 'bg-[#B5541B] text-white shadow-md'
                  : 'bg-white text-[#3B3535]/70 border border-gray-200 hover:border-[#B5541B]/40 hover:text-[#B5541B]'
              }`}
            >
              {sc.tabLabel}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F1E1E] mb-2" data-testid="use-case-title">{s.title}</h3>
            <p className="text-base text-[#3B3535]/70 leading-relaxed mb-6">{s.story}</p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 border border-gray-200" data-testid="use-case-now">
                <h4 className="text-xs uppercase tracking-widest font-bold text-[#3B3535]/50 mb-3">Now</h4>
                <ul className="space-y-2.5">
                  {(s.now || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#3B3535]/75 leading-relaxed">
                      <X className="w-4 h-4 text-[#3B3535]/35 mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#B5541B]/[0.06] rounded-2xl p-5 border border-[#B5541B]/20" data-testid="use-case-with">
                <h4 className="text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-3">With ResiAX</h4>
                <ul className="space-y-2.5">
                  {(s.withResiax || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#3B3535]/85 leading-relaxed">
                      <Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button
              onClick={() => {
                if (s.issueCategory) sessionStorage.setItem('resiax_issue_category', s.issueCategory);
                navigate('/resident-pilot#register');
              }}
              data-testid="use-case-apply-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors group mb-8"
            >
              {d.ctaLabel}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>

            <div className="bg-white rounded-2xl p-5 border border-[#B5541B]/15" data-testid="use-case-boundaries">
              <h4 className="flex items-center gap-2 font-bold text-[#1F1E1E] text-sm mb-3">
                <Info className="w-4 h-4 text-[#B5541B]" />
                {d.boundariesHeading}
              </h4>
              <ul className="space-y-2">
                {(d.boundaries || []).map((item, i) => (
                  <li key={i} className="text-sm text-[#3B3535]/70 leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="relative lg:sticky lg:top-28">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-gray-200/60 bg-white max-w-md mx-auto">
              <img
                key={s.image}
                src={s.image}
                alt={s.imageAlt || 'ResiAX app screenshot'}
                className="w-full h-auto"
                data-testid="use-case-screenshot"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCaseSection;
